import { Inject, Injectable } from '@angular/core';
import { States as VerticalNavState } from '@go-modules/enums/states';
import { Observable, shareReplay, map, filter } from 'rxjs';
import type { Observer, TeardownLogic } from 'rxjs';
import { EnvironmentVarsService } from '../environment-vars/environment-vars.service';
import { ENVIRONMENTS, LTI_ENVIRONMENT_MODES } from '../environment-vars/environments';
import { NavigationEnd } from '@angular/router';
import { Router } from '@angular/router';
import { ROUTE_NAMES, RouterService } from '@go-modules/services/router/router.service';
import { Transition, TransitionService, UIRouterGlobals } from '@uirouter/angularjs';
import { $uiRouterGlobalsToken } from '@go-modules/upgraded-3rd-party-deps/ui-router-globals.upgrade';
import { $transitionsToken } from '@go-modules/upgraded-3rd-party-deps/transitions.upgrade';
@Injectable({
	providedIn: 'root'
})
export class BreadCrumbStateService {

	public breadcrumbsState$: Observable<VerticalNavState>;
	public navState$: Observable<ROUTE_NAMES>;
	public showMobileBackButton$: Observable<boolean>;
	public isLTI: boolean = false;
	public environment;

	private folderViewStates: ROUTE_NAMES[] = [
		ROUTE_NAMES.DASHBOARD_ACTIVITY_VIEW,
		ROUTE_NAMES.DASHBOARD_FOLDERS,
		ROUTE_NAMES.DASHBOARD_FOLDER_VIEW,
		ROUTE_NAMES.DASHBOARD_SESSION_VIEW,
		ROUTE_NAMES.DASHBOARD_ACTIVITY_TASK,
		ROUTE_NAMES.DASHBOARD_ACTIVITY_TASK_REPORT,
		ROUTE_NAMES.DASHBOARD_ACTIVITY_REPORT
	];
	private licenseManagementViewStates: ROUTE_NAMES[] = [
		ROUTE_NAMES.LICENSE_MANAGEMENT,
		ROUTE_NAMES.LICENSE_SEATS_MANAGEMENT
	];
	private archiveViewStates: ROUTE_NAMES[] = [
		ROUTE_NAMES.DASHBOARD_ARCHIVE
	];
	private userSettingStates: ROUTE_NAMES[] = [
		ROUTE_NAMES.DASHBOARD_SETTINGS
	];

	constructor (
		@Inject($uiRouterGlobalsToken) private $uiRouterGlobals: UIRouterGlobals,
		@Inject($transitionsToken) private $transitions: TransitionService,
		private routerService: RouterService,
		private router: Router
	) {
		if (this.routerService.isUsingUIRouter()) {
			this.navState$ = new Observable((observer: Observer<ROUTE_NAMES>) => {
				observer.next(this.$uiRouterGlobals.current.name as ROUTE_NAMES);
				return this.$transitions.onSuccess({}, (transition: Transition) => {
					observer.next(transition.to().name as ROUTE_NAMES);
				}) as TeardownLogic;
			}).pipe(shareReplay({ bufferSize: 1, refCount: true }));
		} else {
			this.navState$ = new Observable((observer: Observer<ROUTE_NAMES>) => {
				observer.next(this.routerService.getCurrentRouteName() as ROUTE_NAMES);
				return this.router.events.pipe(filter((event: any) => event instanceof NavigationEnd))
					.subscribe(() => {
						observer.next(this.routerService.getCurrentRouteName() as ROUTE_NAMES);
					}) as TeardownLogic;
			}).pipe(shareReplay({ bufferSize: 1, refCount: true }));
		}

		this.breadcrumbsState$ = this.navState$.pipe(map((state: ROUTE_NAMES) => this.getStateFromRouter(state)));

		const environmentVarsService = EnvironmentVarsService.getInstance();
		this.environment = environmentVarsService.get(EnvironmentVarsService.VAR.ENVIRONMENT) || {};
		this.isLTI = this.environment.name === ENVIRONMENTS.LTI;
		this.showMobileBackButton$ = this.navState$.pipe(map((state: ROUTE_NAMES) => {
			// always show back button if not in LTI
			if (!this.isLTI) {
				return true;
			}
			// in normal lti, show back button if not in activity view
			if (this.environment.mode === LTI_ENVIRONMENT_MODES.DEFAULT) {
				const activityStates = [
					ROUTE_NAMES.DASHBOARD_ACTIVITY_VIEW,
					ROUTE_NAMES.DASHBOARD_ACTIVITY_TASK,
					ROUTE_NAMES.DASHBOARD_ACTIVITY_REPORT,
					ROUTE_NAMES.DASHBOARD_ACTIVITY_TASK_REPORT
				];
				return !activityStates.includes(state);
			// in course-level lti, show back button if not in folder view
			} else {
				return state !== ROUTE_NAMES.DASHBOARD_FOLDER_VIEW;
			}
		}));
	}

	private getStateFromRouter (state: ROUTE_NAMES): VerticalNavState {
		if (this.folderViewStates.includes(state)) {
			return VerticalNavState.FOLDER_VIEW;
		} else if (this.licenseManagementViewStates.includes(state)) {
			return VerticalNavState.LICENSE_MANAGEMENT_VIEW;
		} else if (this.archiveViewStates.includes(state)) {
			return VerticalNavState.ARCHIVE_VIEW;
		} else if (this.userSettingStates.includes(state)) {
			return VerticalNavState.USER_SETTINGS;
		} else {
			return VerticalNavState.NO_BREADCRUMBS;
		}
	}

}
